import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamCustomersPermissions } from 'containers/app/appSlice';
import { BinIcon, GreenTickIcon, ListActionsIcon, PrintIcon, ShareIcon, VerticalListActionsIcon } from 'assets/icons';
import { PortalPopover } from 'components';
import { PopoverArrowTypes } from 'constants/common';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { CleverTapEventsInvoices } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import { useLazyDownloadPrintInvoiceAsPDFQuery } from '../api';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import { StatusIndicator } from '../constants';
import { InvoiceStatus } from '../invoices.model';

interface InvoiceRowActionsProps {
  invoiceId: string;
  invoiceStatusIndicator: string;
  invoiceStatus: InvoiceStatus;
  isMarkAsPaidEnabled: boolean;
}

const InvoiceRowActions: FC<InvoiceRowActionsProps> = ({
  invoiceId,
  invoiceStatusIndicator,
  isMarkAsPaidEnabled,
  invoiceStatus
}) => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const [showActionsPopover, setShowActionsPopover] = useState(false);
  const [downloadPrintInvoiceAsPDF] = useLazyDownloadPrintInvoiceAsPDFQuery();

  const isPending = invoiceStatusIndicator
    ? invoiceStatusIndicator.toUpperCase() !== StatusIndicator.FULLY_PAID
    : invoiceStatus === InvoiceStatus.PARTIALLY_PAID || invoiceStatus === InvoiceStatus.PENDING;

  const canDelete = invoiceStatusIndicator.toUpperCase() === StatusIndicator.NO_PAYMENTS_YET;
  const canRecordPayment = isMarkAsPaidEnabled && invoiceStatusIndicator.toUpperCase() !== StatusIndicator.FULLY_PAID;

  const isShareEnabled = () => {
    return (
      customerPermissions.viewCustomersCreatedByTeam ||
      customerPermissions.editCustomersCreatedByTeam ||
      customerPermissions.editExistingCustomers
    );
  };

  const handleClose = () => {
    setShowActionsPopover(false);
  };

  const handleShareClick = () => {
    setShowActionsPopover(false);
    navigate(`/invoices/${currentTeam?.id}/${invoiceId}/${isPending ? 'share-pending-invoice' : 'share-invoice'}`, {
      state: ROOT_ROUTE_STATE
    });
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesMMShareInvoice, { 'Invoice Id': invoiceId });
  };

  const handlePrintClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowActionsPopover(false);
    downloadPrintInvoiceAsPDF({ invoiceId });
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesMMPrint, { 'Invoice Id': invoiceId });
  };

  const handleMarkAsPaidClick = () => {
    setShowActionsPopover(false);
    navigate(`/invoices/${currentTeam?.id}/${invoiceId}/record-payment`, { state: ROOT_ROUTE_STATE });
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesMMMarkAsPaid, { 'Invoice Id': invoiceId });
  };

  const handleDeleteClick = () => {
    setShowActionsPopover(false);
    navigate(`/invoices/${currentTeam?.id}/${invoiceId}/delete-invoice`, { state: ROOT_ROUTE_STATE });
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesMMDelete, { 'Invoice Id': invoiceId });
  };

  const listActionsAnchor = ({ isPanelOpen }: { isPanelOpen: boolean }) => (
    <button type="button" id="invoice-table-more-options" className="z-20 rounded-full p-2 hover:bg-gray-100 sm:pl-4">
      <ListActionsIcon
        className={twMerge(
          'hidden shrink-0 lg:flex',
          classnames({
            'fill-current text-secondary': isPanelOpen
          })
        )}
      />
      <VerticalListActionsIcon
        className={twMerge(
          'mx-2 flex shrink-0 lg:hidden',
          classnames({
            'fill-current text-secondary': isPanelOpen
          })
        )}
      />
    </button>
  );

  return (
    <PortalPopover
      anchorComponent={listActionsAnchor}
      show={showActionsPopover}
      onTogglePopover={() => setShowActionsPopover(!showActionsPopover)}
      onClosePopover={handleClose}
      highlightAnchor={false}
      offset={{ right: 40, top: 20 }}
      padding="p-2"
      width="w-[200px]"
      minWidth="min-w-[200px]"
      showArrow={false}
      arrowType={PopoverArrowTypes.TOP}
      arrowOffset="right-6"
      containerClassName="border border-secondaryBtn p-3"
      anchorComponentId="invoice-table-more-options">
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        {isShareEnabled() && (
          <div
            id="invoice-table-3dot-share-invoice"
            className="flex cursor-pointer flex-row items-center gap-2"
            onClick={handleShareClick}>
            <ShareIcon />
            <div>Share Invoice</div>
          </div>
        )}
        <div
          id="invoice-table-3dot-print-invoice"
          onClick={handlePrintClick}
          className="flex cursor-pointer flex-row items-center gap-2">
          <PrintIcon />
          <div>Print</div>
        </div>
        {canRecordPayment && (
          <div
            id="invoice-table-3dot-record-payment"
            className="flex cursor-pointer flex-row items-center gap-2"
            onClick={handleMarkAsPaidClick}>
            <GreenTickIcon />
            <div>Record a payment</div>
          </div>
        )}
        {canDelete && (
          <div
            id="invoice-table-3dot-delete-invoice"
            className="flex cursor-pointer flex-row items-center gap-2"
            onClick={handleDeleteClick}>
            <BinIcon />
            <div>Delete</div>
          </div>
        )}
      </div>
    </PortalPopover>
  );
};

export default InvoiceRowActions;
